/**
 * HOW someone came in or went out, on a manually registered passage.
 *
 * A manual entrada is written by a porteiro for a passage no device saw — the resident walked past
 * the desk, the gate was already open, a car came in behind another. The event it produced said
 * only "entrada", which is the one thing nobody had to be told; what the record was missing is the
 * part only the person at the desk knows. Three months later, reading the history, "entrada às
 * 19:42" and "entrada a pé às 19:42" are very different records — and when a car is involved, the
 * plate is the difference between a log and an answer.
 *
 * Deliberately a small, fixed list. A free-text box would be filled with "entrou", "ok" and "-",
 * and the one thing worth searching on (the plate) would never be in a consistent place.
 */

export type ManualAccessModeId = 'a_pe' | 'veiculo' | 'aplicativo' | 'bicicleta';

export interface ManualAccessMode {
  id: ManualAccessModeId;
  label: string;
  /** Verb fragment used to build the sentence: "Entrou <fragment>". */
  entrada: string;
  saida: string;
  /** Whether this mode is ABOUT a vehicle, and therefore wants a plate. */
  wantsPlate: boolean;
  /** Whether the plate is required to register, or merely useful. */
  plateRequired: boolean;
  /** Whether the person's own registered vehicles are offered. */
  offersOwnVehicles: boolean;
  /**
   * Whether to ask if the person was DRIVING.
   *
   * Only for their own vehicle. In an Uber or a taxi they are the passenger by definition, and on a
   * bicycle the question means nothing — asking anyway invites an answer that carries no
   * information. Driving matters because it is the difference between "the car is inside" and
   * "the car dropped them and left".
   */
  asksDriver: boolean;
}

export const MANUAL_ACCESS_MODES: ManualAccessMode[] = [
  {
    id: 'a_pe',
    label: 'A pé',
    entrada: 'a pé',
    saida: 'a pé',
    wantsPlate: false,
    plateRequired: false,
    offersOwnVehicles: false,
    asksDriver: false,
  },
  {
    id: 'veiculo',
    label: 'De veículo',
    entrada: 'de veículo',
    saida: 'de veículo',
    // A vehicle passage without a plate is the record that is worth nothing later: it is exactly
    // what somebody comes back to the history looking for.
    wantsPlate: true,
    plateRequired: true,
    offersOwnVehicles: true,
    asksDriver: true,
  },
  {
    id: 'aplicativo',
    label: 'Aplicativo / Táxi',
    entrada: 'de aplicativo',
    saida: 'de aplicativo',
    // Useful when the porteiro caught it, but a rideshare plate is often not written down, and
    // demanding it would push operators into typing "000" to get past the form.
    wantsPlate: true,
    plateRequired: false,
    offersOwnVehicles: false,
    asksDriver: false,
  },
  {
    id: 'bicicleta',
    label: 'Bicicleta / Moto',
    entrada: 'de bicicleta ou moto',
    saida: 'de bicicleta ou moto',
    wantsPlate: true,
    plateRequired: false,
    offersOwnVehicles: false,
    asksDriver: false,
  },
];

export function manualAccessMode(id: ManualAccessModeId): ManualAccessMode {
  return MANUAL_ACCESS_MODES.find((m) => m.id === id) || MANUAL_ACCESS_MODES[0];
}

/** Plates are stored and compared uppercase, without separators the operator may have typed. */
export function normalizePlate(plate: string | null | undefined): string {
  return (plate || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
}

export interface ManualAccessSelection {
  mode: ManualAccessModeId;
  /** Typed or chosen plate, in whatever shape the operator entered it. */
  plate?: string | null;
  /** Set when the plate came from one of the person's registered vehicles. */
  vehicleId?: string | null;
  /**
   * Was this person DRIVING, or a passenger?
   *
   * Only meaningful for their own vehicle. Defaults to driving: a resident arriving in their own
   * car is at the wheel far more often than not, and the common case should need no extra click.
   */
  isDriver?: boolean;
}

/**
 * Whether this selection can be registered.
 *
 * One thing blocks it: a vehicle with no plate. That is the record that would lose the only part
 * worth keeping — it is exactly what somebody comes back to the history looking for.
 */
export function manualAccessIsComplete(selection: ManualAccessSelection): boolean {
  const mode = manualAccessMode(selection.mode);
  if (mode.plateRequired && normalizePlate(selection.plate).length === 0) return false;
  return true;
}

/**
 * The sentence that goes on the event: "Entrou de veículo ABC1D23".
 *
 * Written as prose rather than a code because this is read by a person scrolling a history months
 * later, next to events produced by devices that describe themselves in words too.
 */
export function manualAccessDescription(
  direction: 'entrada' | 'saida',
  selection: ManualAccessSelection,
): string {
  const mode = manualAccessMode(selection.mode);
  const verb = direction === 'entrada' ? 'Entrou' : 'Saiu';
  const fragment = direction === 'entrada' ? mode.entrada : mode.saida;
  const plate = normalizePlate(selection.plate);

  // "(passageiro)" only when it is NOT the default. A line saying "motorista" on every single car
  // passage stops being read, and the one that matters — somebody who was dropped off, so no car
  // came in — would be lost in it.
  const role = mode.asksDriver && selection.isDriver === false ? ' (passageiro)' : '';

  const withPlate = mode.wantsPlate && plate ? `${verb} ${fragment} ${plate}` : `${verb} ${fragment}`;
  return `${withPlate}${role}`.replace(/\s+/g, ' ').trim();
}

/**
 * The body sent with the manual-entry/exit request.
 *
 * Every field is optional on the wire: an older backend ignores the body entirely (that is the
 * state today — the handler takes no payload), and the UI must still work. Sending it now means
 * the day the backend starts reading it, already-released clients are already providing it.
 */
export function manualAccessPayload(
  direction: 'entrada' | 'saida',
  selection: ManualAccessSelection,
): Record<string, unknown> {
  const mode = manualAccessMode(selection.mode);
  const plate = normalizePlate(selection.plate);
  return {
    access_mode: selection.mode,
    description: manualAccessDescription(direction, selection),
    ...(plate ? { vehicle_plate: plate } : {}),
    ...(selection.vehicleId ? { vehicle_id: selection.vehicleId } : {}),
    // Sent only where the question was asked, so the backend never has to guess whether `false`
    // means "was a passenger" or "nobody said".
    ...(mode.asksDriver ? { is_driver: selection.isDriver !== false } : {}),
  };
}
